import React from "react";
import { NavDropdown } from "react-bootstrap";
import { utcToDriverEventDate } from "../utils/dateConvert";

const presentationFor = (totalCount, unacknowledgedCount) => {
  const acknowledgedCount = Math.max(0, totalCount - unacknowledgedCount);
  if (totalCount === 0) return {
    state: "NONE",
    heading: "No recent incidents",
    subtext: "There are no incidents requiring attention or follow-up.",
  };
  if (unacknowledgedCount > 0 && acknowledgedCount === 0) return {
    state: "UNACKNOWLEDGED_ONLY",
    heading: unacknowledgedCount === 1 ? "Incident requiring attention" : "Incidents requiring attention",
    subtext: unacknowledgedCount === 1
      ? "This incident is awaiting acknowledgement and follow-up."
      : "One or more incidents are awaiting acknowledgement and follow-up.",
  };
  if (acknowledgedCount > 0 && unacknowledgedCount === 0) return {
    state: "ACKNOWLEDGED_ONLY",
    heading: acknowledgedCount === 1 ? "Recent incident" : "Recent incidents",
    subtext: acknowledgedCount === 1
      ? "This incident has been acknowledged and is being followed up."
      : "These incidents have been acknowledged and are being followed up.",
  };
  return {
    state: "MIXED",
    heading: "Incidents requiring attention & recent reports",
    subtext: "Some incidents require acknowledgement; others are already being followed up.",
  };
};

export default function RecentIncidents({ data, failed, loading, onMore }) {
  const openIncident = incident => {
    const href = `/realTime?assetId=${encodeURIComponent(incident.assetId)}&incidentId=${encodeURIComponent(incident.incidentId)}`;
    // Always launch synchronously from the tab receiving the user's click.
    const incidentWindow = window.open(href, "_blank");
    if (incidentWindow) {
      incidentWindow.focus();
    }
  };
  const count = data ? data.total : null;
  const pending = data ? Math.max(typeof data.unacknowledgedTotal === "number" ? data.unacknowledgedTotal : data.total, (data.items || []).filter(i => !i.acknowledgedAt).length) : 0;
  const presentation = data ? presentationFor(count, Math.min(count, pending)) : {
    state: "LOADING",
    heading: failed ? "Recent incidents unavailable" : "Recent incidents",
    subtext: failed ? "Unable to refresh incidents. Retrying automatically." : "Loading recent incidents…",
  };
  const label = failed ? "Recent incidents unavailable; retrying" : count === null ? "Loading recent incidents" : `${count} incidents, ${pending} awaiting acknowledgement`;
  return (
    <NavDropdown id="recent-incidents-dropdown" align="end" className={`recent-incidents ${pending > 0 ? "has-incidents" : count > 0 ? "all-acknowledged" : "quiet"} ${failed ? "degraded" : ""}`}
      title={<span className="recent-incidents-trigger" title={label}><span aria-hidden="true">INCIDENTS <strong>{failed ? "!" : count === null ? "—" : count}</strong></span><span className="visually-hidden">{label}</span></span>}>
      <div className="recent-incidents-heading" data-state={presentation.state}>{presentation.heading}<small>{presentation.subtext}</small></div>
      <div className="recent-incidents-status" role="status">
        {failed ? "Unable to refresh incidents. Retrying automatically." : !data ? "Loading incidents…" : ""}
        {failed && data ? " Showing the last successful result." : ""}
      </div>
      <div className="recent-incidents-list">
        {(data?.items || []).map(incident => (
          <div className={`recent-incidents-row ${incident.acknowledgedAt ? "acknowledged" : "unacknowledged"}`} key={incident.incidentId}>
            <div className="recent-incidents-summary">
              <div className="recent-incidents-reference">
                <strong>{incident.incidentReference}</strong>
                <time dateTime={incident.submittedAt}>{utcToDriverEventDate(incident.submittedAt) || incident.submittedAt}</time>
              </div>
              <span className="recent-incidents-asset">{[incident.assetName, incident.friendlyName, incident.registration].filter((v, i, values) => v && values.indexOf(v) === i).join(" — ")}</span>
              <small>{String(incident.incidentType || "").replace(/_/g, " ")} · {incident.driverName}</small>
              {incident.customerName && <small>{incident.customerName}</small>}
              <small className="recent-incidents-ack-status">{incident.acknowledgedAt
                ? `Acknowledged by ${incident.acknowledgedByName || incident.acknowledgedById || "recorded user"} · ${utcToDriverEventDate(incident.acknowledgedAt)}`
                : "Awaiting acknowledgement"}</small>
            </div>
            <NavDropdown.Item as="button" type="button" onClick={() => openIncident(incident)}
              title="Open incident in a new tab"
              aria-label={`View Incident ${incident.incidentReference} (opens in a new tab)`} className="recent-incidents-view">View Incident</NavDropdown.Item>
          </div>
        ))}
        {data?.hasMore && <button type="button" className="recent-incidents-more" disabled={loading} onClick={onMore}>{loading ? "Loading…" : "Load more incidents"}</button>}
      </div>
    </NavDropdown>
  );
}
